import { initReactI18next } from 'react-i18next';
import i18n from 'i18next';
import enUI from '@linktivity/link-ui/lang/en';
import jaUI from '@linktivity/link-ui/lang/ja';
import koUI from '@linktivity/link-ui/lang/ko';
import zhUI from '@linktivity/link-ui/lang/zh';
import ztUI from '@linktivity/link-ui/lang/zt';
import { JAPANESE_LOCALE, LOCALE_OPTIONS } from '@link/share/constants';
import { LocaleOption } from '@link/share/services/types';
import enCountry from './country/en';
import jaCountry from './country/ja';
import koCountry from './country/ko';
import zhCountry from './country/zh';
import ztCountry from './country/zt';
import enJapanPrefecture from './japanPrefectures/en';
import jaJapanPrefecture from './japanPrefectures/ja';
import koJapanPrefecture from './japanPrefectures/ko';
import zhJapanPrefecture from './japanPrefectures/zh';
import ztJapanPrefecture from './japanPrefectures/zt';
import shareEnText from './lang/en';
import shareJaText from './lang/ja';
import shareKoText from './lang/ko';
import shareZhText from './lang/zh';
import shareZtText from './lang/zt';
import enLang from './language/en';
import jaLang from './language/ja';
import koLang from './language/ko';
import zhLang from './language/zh';
import ztLang from './language/zt';

export const DEFAULT_LOCALE = JAPANESE_LOCALE;
export const LOCALE_LIST: string[] = LOCALE_OPTIONS.map(locale => locale.value);

export const getLocaleValues = (options: readonly LocaleOption[]): string[] =>
  options.map(locale => locale.value);

interface ResourcesRecord {
  [key: string]: string | string[] | ResourcesRecord | Record<string, unknown>;
}

interface Resources {
  jaText?: ResourcesRecord;
  enText?: ResourcesRecord;
  koText?: ResourcesRecord;
  zhText?: ResourcesRecord;
  ztText?: ResourcesRecord;
}

declare module 'i18next' {
  interface CustomTypeOptions {
    returnNull: false;
  }
}

export const resources = ({
  jaText,
  enText,
  koText,
  zhText,
  ztText
}: Resources) => {
  return {
    ja: {
      translation: {
        ...shareJaText,
        ...jaText,
        ...jaUI,
        country: {
          ...jaCountry
        },
        prefecture: {
          ...jaJapanPrefecture
        },
        lang: jaLang
      }
    },
    en: {
      translation: {
        ...shareEnText,
        ...enText,
        ...enUI,
        country: {
          ...enCountry
        },
        prefecture: {
          ...enJapanPrefecture
        },
        lang: enLang
      }
    },
    ko: {
      translation: {
        ...shareKoText,
        ...koText,
        ...koUI,
        country: {
          ...koCountry
        },
        prefecture: {
          ...koJapanPrefecture
        },
        lang: koLang
      }
    },
    zh: {
      translation: {
        ...shareZhText,
        ...zhText,
        ...zhUI,
        country: {
          ...zhCountry
        },
        prefecture: {
          ...zhJapanPrefecture
        },
        lang: zhLang
      }
    },
    zt: {
      translation: {
        ...shareZtText,
        ...ztText,
        ...ztUI,
        country: {
          ...ztCountry
        },
        prefecture: {
          ...ztJapanPrefecture
        },
        lang: ztLang
      }
    }
  };
};

export function setLocale(locale: string): string {
  void i18n.changeLanguage(locale);
  document.documentElement.setAttribute('lang', locale);
  localStorage.setItem('locale', locale);
  return locale;
}

function getLocaleParams(supportedLocales: string[]) {
  const searchParams = new URLSearchParams(location.search);
  const lang = searchParams.get('lang') || '';
  if (supportedLocales.includes(lang)) {
    return lang;
  }
}

interface InitI18nOptions extends Resources {
  defaultLocale?: string;
  supportedLocales?: string[];
}

export const initI18n = ({
  jaText,
  enText,
  koText,
  zhText,
  ztText,
  defaultLocale = DEFAULT_LOCALE,
  supportedLocales = LOCALE_LIST
}: InitI18nOptions) => {
  void i18n.use(initReactI18next).init({
    lng: defaultLocale,
    returnNull: false,
    // React escapes text nodes already; i18next's escaping would turn an
    // interpolated "9/15 (Mon)" into "9&#x2F;15 (Mon)".
    interpolation: {
      escapeValue: false
    },
    resources: resources({
      jaText,
      enText,
      koText,
      zhText,
      ztText
    })
  });

  const resolvedLocale =
    getLocaleParams(supportedLocales) ||
    localStorage.getItem('locale') ||
    defaultLocale;
  setLocale(
    supportedLocales.includes(resolvedLocale) ? resolvedLocale : defaultLocale
  );
};

export default i18n;
